import { Controller } from "stimulus"

// Polls the publish-statuses endpoint while any menu is mid-publish and updates
// the per-card status banner, Draft/Published chip, and Publish/Revert buttons
// in place, so a publish result shows up without a manual page refresh. Stops
// polling once nothing is processing.
export default class extends Controller {
  static targets = ["status", "chip", "publishButton", "revertButton"]
  static values = {
    url: String,
    interval: { type: Number, default: 4000 },
  }

  connect() {
    this.timer = null
    if (this.anyProcessing()) this.start()
  }

  // Enqueues a publish without a full page reload: fires the POST as JSON,
  // switches the button to a disabled spinner, shows the processing banner
  // straight away, and lets the poller pick up the result.
  async publish(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (button.disabled) return

    button.disabled = true
    button.classList.add("loading")

    let ok = false
    try {
      const response = await fetch(button.dataset.publishUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        credentials: "same-origin",
      })
      ok = response.ok
    } catch (error) {
      ok = false
    }

    const status = this.statusFor(button.dataset.menuId)
    if (!ok) {
      button.disabled = false
      button.classList.remove("loading")
      if (status) this.applyStatus(status, { state: "failed", message: "Publish failed to start. Please try again." })
      return
    }

    if (status) this.applyStatus(status, { state: "processing", message: "Publishing… check back shortly." })
    this.start()
  }

  statusFor(menuId) {
    return this.statusTargets.find((el) => el.dataset.menuId === String(menuId))
  }

  disconnect() {
    this.stop()
  }

  start() {
    if (this.timer) return
    this.timer = setInterval(() => this.poll(), this.intervalValue)
  }

  stop() {
    if (!this.timer) return
    clearInterval(this.timer)
    this.timer = null
  }

  anyProcessing() {
    return this.statusTargets.some((el) => el.classList.contains("processing"))
  }

  async poll() {
    let payload
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return
      payload = await response.json()
    } catch (error) {
      return
    }

    this.statusTargets.forEach((el) => this.applyStatus(el, payload[el.dataset.menuId]))
    this.chipTargets.forEach((el) => this.applyChip(el, payload[el.dataset.menuId]))
    this.publishButtonTargets.forEach((el) => this.applyButton(el, payload[el.dataset.menuId], "publishable"))
    this.revertButtonTargets.forEach((el) => this.applyButton(el, payload[el.dataset.menuId], "revertable"))

    if (!this.anyProcessing()) this.stop()
  }

  applyStatus(el, status) {
    if (!status) return

    if (status.state === "none") {
      el.hidden = true
      el.classList.remove("processing", "succeeded", "failed")
      el.textContent = ""
      return
    }

    el.hidden = false
    el.classList.remove("processing", "succeeded", "failed")
    el.classList.add(status.state)
    if (status.message != null) el.textContent = status.message
  }

  applyChip(el, status) {
    if (!status || status.publishable == null) return

    const draftChip = el.querySelector(".menu-cards2-status-chip.draft")
    const publishedChip = el.querySelector(".menu-cards2-status-chip.published")
    if (draftChip) draftChip.hidden = !status.publishable
    if (publishedChip) publishedChip.hidden = status.publishable
  }

  applyButton(el, status, flag) {
    if (!status || status[flag] == null) return

    el.hidden = !status[flag]
    if (status.state !== "processing") {
      el.disabled = false
      el.classList.remove("loading")
    }
  }
}
